import { useState, useEffect, useMemo } from 'react'

/**
 * Constantes de breakpoints compartilhadas para toda a aplicação
 * 
 * Reason: Centralizar breakpoints evita inconsistências entre componentes
 */
export const BREAKPOINTS = {
  smallMobile: 480,
  mobile: 768,
  tablet: 1024,
  desktop: 1200
} as const

/**
 * Alturas dos componentes fixos do header
 * 
 * Reason: Centralizar alturas garante que os componentes não se sobreponham
 */
// Reason: As alturas devem corresponder à altura REAL visual dos componentes
// O pr-header tem altura reduzida no mobile devido ao scaleY no CSS
// Incluímos espaçamento extra no mobile para evitar sobreposição
export const HEADER_HEIGHTS = {
  headerEstadual: {
    smallMobile: 44,  // Altura visual (~28px) + espaçamento extra (14px)
    mobile: 56,       // Altura visual (~28px) + espaçamento extra (14px)
    desktop: 46
  },
  logoSection: {
    smallMobile: 55,
    mobile: 65,
    desktop: 80
  },
  navbar: {
    smallMobile: 44,
    mobile: 50,
    desktop: 60
  }
} as const

/**
 * Interface para o retorno do hook useResponsive
 */
export interface ResponsiveState {
  windowWidth: number
  isSmallMobile: boolean
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  // Alturas calculadas para cada breakpoint
  headerEstadualHeight: number
  logoSectionHeight: number
  navbarHeight: number
  // Altura total do header fixo (para espaçador de conteúdo)
  totalFixedHeaderHeight: number
}

/**
 * Hook centralizado para responsividade
 * 
 * Fornece:
 * - Flags de breakpoints (isSmallMobile, isMobile, etc.)
 * - Alturas calculadas para cada componente do header
 * - Altura total para espaçador de conteúdo
 * 
 * Returns:
 *   Objeto com todas as informações de responsividade
 */
export function useResponsive(): ResponsiveState {
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  )

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    // Inicializa com o valor correto
    handleResize()
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return useMemo(() => {
    const isSmallMobile = windowWidth <= BREAKPOINTS.smallMobile
    const isMobile = windowWidth <= BREAKPOINTS.mobile
    const isTablet = windowWidth > BREAKPOINTS.mobile && windowWidth <= BREAKPOINTS.tablet
    const isDesktop = windowWidth > BREAKPOINTS.mobile

    // Calcula alturas baseadas no breakpoint
    const headerEstadualHeight = isMobile 
      ? HEADER_HEIGHTS.headerEstadual.mobile 
      : HEADER_HEIGHTS.headerEstadual.desktop

    const logoSectionHeight = isSmallMobile 
      ? HEADER_HEIGHTS.logoSection.smallMobile 
      : isMobile 
        ? HEADER_HEIGHTS.logoSection.mobile 
        : HEADER_HEIGHTS.logoSection.desktop

    const navbarHeight = isSmallMobile 
      ? HEADER_HEIGHTS.navbar.smallMobile 
      : isMobile 
        ? HEADER_HEIGHTS.navbar.mobile 
        : HEADER_HEIGHTS.navbar.desktop

    // Altura total do header fixo para usar como espaçador
    const totalFixedHeaderHeight = headerEstadualHeight + logoSectionHeight + navbarHeight

    return {
      windowWidth,
      isSmallMobile,
      isMobile,
      isTablet,
      isDesktop,
      headerEstadualHeight,
      logoSectionHeight,
      navbarHeight,
      totalFixedHeaderHeight
    }
  }, [windowWidth])
}

/**
 * Hook para detectar visibilidade baseada no scroll
 * 
 * Returns:
 *   Boolean indicando se o usuário está no topo da página
 */
export function useScrollAtTop(): boolean {
  const [isAtTop, setIsAtTop] = useState(true)

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY <= 0)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return isAtTop
}

